import { deleteDoc, doc, serverTimestamp, updateDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firestoreDb } from './firebase'
import { firebaseFunctions } from './firebase'
import { sendFormRequest } from './formRequestService'
import { deleteWorkspaceFile } from './storageHubService'
import { deleteStockItem } from './stockService'
import { isNoSignInPreviewActive } from '../debug'
import type { FormRequestSourceItem } from '../types/forms'

export type AttachableItemKind = 'GOAL' | 'TASK' | 'SUBTASK' | 'CHECKLIST'

export type ItemAttachmentTarget = {
  id: string
  workspaceId: string
  title: string
  kind: AttachableItemKind
  collectionName: 'elements' | 'checklists'
  attachedFormIds?: string[]
  attachedDesignIds?: string[]
  attachedUploadIds?: string[]
  attachedLinkIds?: string[]
  assigneeIds?: string[]
}

export const sourceItemForTarget = (target: Pick<ItemAttachmentTarget, 'id' | 'kind' | 'title'>): FormRequestSourceItem => ({
  itemId: target.id,
  itemType: target.kind,
  itemTitle: target.title,
})

export const attachFormToItem = async (target: ItemAttachmentTarget, formId: string) => {
  const nextFormIds = Array.from(new Set([...(target.attachedFormIds ?? []), formId]))
  await updateDoc(doc(firestoreDb, target.collectionName, target.id), {
    attachedFormIds: nextFormIds,
    updatedAt: serverTimestamp(),
  })
  await Promise.all((target.assigneeIds ?? []).map((recipientId) => sendFormRequest({
    workspaceId: target.workspaceId,
    formId,
    recipientId,
    sourceItem: sourceItemForTarget(target),
  }).catch((error) => {
    console.warn('[itemAttachmentService] form assignment propagation failed', error)
  })))
}

export const detachFormFromItem = async (target: ItemAttachmentTarget, formId: string) => {
  const nextFormIds = (target.attachedFormIds ?? []).filter((id) => id !== formId)
  await updateDoc(doc(firestoreDb, target.collectionName, target.id), {
    attachedFormIds: nextFormIds,
    updatedAt: serverTimestamp(),
  })
  const assigneeIds = target.assigneeIds ?? []
  if (assigneeIds.length === 0) return
  const callable = httpsCallable<{
    workspaceId: string
    formId: string
    recipientId: string
    sourceItemId: string
  }, { requestId: string }>(firebaseFunctions, 'cancelFormRequestSource')
  await Promise.all(assigneeIds.map((recipientId) => callable({
    workspaceId: target.workspaceId,
    formId,
    recipientId,
    sourceItemId: target.id,
  }).catch((error) => {
    console.warn('[itemAttachmentService] detached form cancellation failed', error)
  })))
}

export const attachDesignToItem = async (target: ItemAttachmentTarget, designId: string) => {
  if (isNoSignInPreviewActive()) return
  const nextDesignIds = Array.from(new Set([...(target.attachedDesignIds ?? []), designId]))
  await updateDoc(doc(firestoreDb, target.collectionName, target.id), {
    attachedDesignIds: nextDesignIds,
    updatedAt: serverTimestamp(),
  })
}

export const detachDesignFromItem = async (target: ItemAttachmentTarget, designId: string) => {
  if (isNoSignInPreviewActive()) return
  const nextDesignIds = (target.attachedDesignIds ?? []).filter((id) => id !== designId)
  await updateDoc(doc(firestoreDb, target.collectionName, target.id), {
    attachedDesignIds: nextDesignIds,
    updatedAt: serverTimestamp(),
  })
}

export const attachUploadToItem = async (target: ItemAttachmentTarget, uploadId: string) => {
  if (isNoSignInPreviewActive()) return
  const nextUploadIds = Array.from(new Set([...(target.attachedUploadIds ?? []), uploadId]))
  await updateDoc(doc(firestoreDb, target.collectionName, target.id), {
    attachedUploadIds: nextUploadIds,
    updatedAt: serverTimestamp(),
  })
}

export const detachUploadFromItem = async (target: ItemAttachmentTarget, uploadId: string) => {
  if (isNoSignInPreviewActive()) return
  const nextUploadIds = (target.attachedUploadIds ?? []).filter((id) => id !== uploadId)
  await updateDoc(doc(firestoreDb, target.collectionName, target.id), {
    attachedUploadIds: nextUploadIds,
    updatedAt: serverTimestamp(),
  })
}

export const attachLinkToItem = async (target: ItemAttachmentTarget, linkId: string) => {
  if (isNoSignInPreviewActive()) return
  const nextLinkIds = Array.from(new Set([...(target.attachedLinkIds ?? []), linkId]))
  await updateDoc(doc(firestoreDb, target.collectionName, target.id), {
    attachedLinkIds: nextLinkIds,
    updatedAt: serverTimestamp(),
  })
}

export const detachLinkFromItem = async (target: ItemAttachmentTarget, linkId: string) => {
  if (isNoSignInPreviewActive()) return
  const nextLinkIds = (target.attachedLinkIds ?? []).filter((id) => id !== linkId)
  await updateDoc(doc(firestoreDb, target.collectionName, target.id), {
    attachedLinkIds: nextLinkIds,
    updatedAt: serverTimestamp(),
  })
}

export const unassignAttachedFormsForItem = async (target: ItemAttachmentTarget, assigneeIds: string[]) => {
  const formIds = target.attachedFormIds ?? []
  if (formIds.length === 0 || assigneeIds.length === 0) return
  const callable = httpsCallable<{
    workspaceId: string
    formId: string
    recipientId: string
    sourceItemId: string
  }, { requestId: string }>(firebaseFunctions, 'cancelFormRequestSource')
  await Promise.all(formIds.flatMap((formId) => assigneeIds.map((recipientId) => callable({
    workspaceId: target.workspaceId,
    formId,
    recipientId,
    sourceItemId: target.id,
  }).catch((error) => {
    console.warn('[itemAttachmentService] attached form cancellation failed', error)
  }))))
}

export const assignAttachedFormsForItem = async (target: ItemAttachmentTarget, assigneeIds: string[]) => {
  const formIds = target.attachedFormIds ?? []
  if (formIds.length === 0 || assigneeIds.length === 0) return
  await Promise.all(formIds.flatMap((formId) => assigneeIds.map((recipientId) => sendFormRequest({
    workspaceId: target.workspaceId,
    formId,
    recipientId,
    sourceItem: sourceItemForTarget(target),
  }).catch((error) => {
    console.warn('[itemAttachmentService] attached form assignment failed', error)
  }))))
}

export const detachProvisionFromItem = async (target: ItemAttachmentTarget, kind: string, id: string) => {
  const normalized = kind.toLowerCase()
  if (normalized.startsWith('form')) {
    return detachFormFromItem(target, id)
  }
  if (normalized.startsWith('design')) {
    return detachDesignFromItem(target, id)
  }
  if (normalized.startsWith('upload')) {
    return detachUploadFromItem(target, id)
  }
  if (normalized.startsWith('link')) {
    return detachLinkFromItem(target, id)
  }
}

export const deleteWorkspaceProvisionPermanently = async ({
  workspaceId,
  kind,
  id,
  target,
  actor,
}: {
  workspaceId: string
  kind: string
  id: string
  target?: ItemAttachmentTarget
  actor: { id: string; name: string }
}) => {
  if (isNoSignInPreviewActive()) return
  if (target) {
    await detachProvisionFromItem(target, kind, id)
  }
  const normalized = kind.toLowerCase()
  if (normalized.startsWith('form')) {
    const ref = doc(firestoreDb, 'form_definitions', id)
    await deleteDoc(ref).catch(() => undefined)
  } else if (normalized.startsWith('design')) {
    const ref = doc(firestoreDb, 'design_assets', id)
    await deleteDoc(ref).catch(() => undefined)
  } else if (normalized.startsWith('upload')) {
    await deleteWorkspaceFile(id, 'Deleted from provisions picker').catch(() => undefined)
  } else if (normalized === 'stock') {
    await deleteStockItem(workspaceId, id, { actorId: actor.id, actorName: actor.name }).catch(() => undefined)
  }
}

